import { selectLoading, setLoading } from "state/slices/loaderSlice";
import { useFormik } from "formik";
import { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import callApi from "services/api";
import * as Yup from "yup";
import { setLoginView } from "state/slices/loginViewSlice";
import { setAlertMessage } from "state/slices/alertSlice";
import { setLoginStatus } from "state/slices/loginSlice";
import {
  generateES6Hash,
  generateSHA512HashAndEncryptInES6,
} from "utils/Encryption";

const ChangePassword = () => {
  const navigate = useNavigate();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);

  const togglePasswordVisibility = (type) => {
    if (type === "old") setShowOldPassword((prev) => !prev);
    if (type === "new") setShowPassword((prev) => !prev);
    if (type === "confirm") setShowConfirmPassword((prev) => !prev);
  };

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Old password is required."),
      newPassword: Yup.string()
        .min(8, "Password must be at least 8 characters.")
        .required("New password is required."),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match.")
        .required("Confirm password is required."),
    }),
    onSubmit: async (values) => {
      const encryptedData = {
        oldPassword: generateES6Hash(values?.oldPassword),
        newPassword: generateES6Hash(values?.newPassword),
      };
      dispatch(setLoading(true));
      var response = await callApi(
        "/updatePassword",
        "POST",
        encryptedData,
        true,
        true,
        dispatch,
        navigate
      );
      dispatch(setLoading(false));
      setLoading(false);
      if (response.success) {
        navigate("/");
        dispatch(setLoginView("login"));
        dispatch(
          setAlertMessage(
            "Your password has been updated successfully. Please login again.|true"
          )
        );
        localStorage.removeItem("token");
        dispatch(setLoginStatus(false));
      }
    },
  });

  return (
    <>
      <div className="row justify-content-center">
        <div className="col-md-8">
          <div className="card mt-3">
            <div className="card-face card-front">
              <div className="card-body">
                <div className="text-center mb-2">
                  <b className="card-title">Change Password</b>
                </div>
                <form onSubmit={formik.handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="oldPassword" className="form-label">
                      Old Password<span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type={showOldPassword ? "text" : "password"}
                        id="oldPassword"
                        name="oldPassword"
                        className={`form-control ${
                          formik.touched.oldPassword &&
                          formik.errors.oldPassword
                            ? "is-invalid"
                            : ""
                        }`}
                        {...formik.getFieldProps("oldPassword")}
                        maxLength={50}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility("old")}
                      >
                        {showOldPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      {formik.touched.oldPassword &&
                        formik.errors.oldPassword && (
                          <div className="invalid-feedback">
                            {formik.errors.oldPassword}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* New Password */}
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      New Password<span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="newPassword"
                        className={`form-control ${
                          formik.touched.newPassword &&
                          formik.errors.newPassword
                            ? "is-invalid"
                            : ""
                        }`}
                        {...formik.getFieldProps("newPassword")}
                        maxLength={50}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility("new")}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      {formik.touched.newPassword &&
                        formik.errors.newPassword && (
                          <div className="invalid-feedback">
                            {formik.errors.newPassword}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="mb-3">
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm Password<span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        name="confirmPassword"
                        className={`form-control ${
                          formik.touched.confirmPassword &&
                          formik.errors.confirmPassword
                            ? "is-invalid"
                            : ""
                        }`}
                        {...formik.getFieldProps("confirmPassword")}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => togglePasswordVisibility("confirm")}
                      >
                        {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      {formik.touched.confirmPassword &&
                        formik.errors.confirmPassword && (
                          <div className="invalid-feedback">
                            {formik.errors.confirmPassword}
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="d-grid">
                    <button
                      type="submit"
                      className="btn btn-info"
                      disabled={loading}
                    >
                      Change Password
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="d-flex justify-content-center mt-2">
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangePassword;
